import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserCog, Users, AlertCircle } from 'lucide-react';

export default function AuthCallback() {
  const { processGoogleAuth, updateRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [needsRole, setNeedsRole] = useState(false);
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Try hash first, then query params (Safari may convert hash to query on redirect)
    let sessionId = null;
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      sessionId = hashParams.get('session_id');
    }
    if (!sessionId) {
      sessionId = searchParams.get('session_id');
    }

    if (!sessionId) {
      setError('No session ID received from Google. Please try signing in again.');
      setProcessing(false);
      return;
    }

    const exchangeSession = async (retries = 2) => {
      try {
        const userData = await processGoogleAuth(sessionId);
        if (userData.needs_role) {
          setNeedsRole(true);
          setProcessing(false);
        } else {
          window.history.replaceState(null, '', '/dashboard');
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        const status = err.response?.status;
        // Retry on 502/504 (gateway timeouts)
        if ((status === 502 || status === 504) && retries > 0) {
          setTimeout(() => exchangeSession(retries - 1), 1500);
          return;
        }
        let msg = 'Sign-in failed. Please try again.';
        if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        } else if (status === 502 || status === 504) {
          msg = 'Server is temporarily busy. Please wait a moment and try signing in again.';
        } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
          msg = 'Connection issue. Please try signing in again from the login page.';
        } else if (err.message) {
          msg = err.message;
        }
        setError(msg);
        setProcessing(false);
      }
    };

    // Small delay to let Safari settle after cross-site redirect
    setTimeout(exchangeSession, 100);
  }, [processGoogleAuth, navigate, searchParams]);

  const handleRoleSelect = async (role) => {
    try {
      await updateRole(role);
      window.history.replaceState(null, '', '/dashboard');
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Failed to set role. Please try again.');
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FDFA]" data-testid="auth-callback-loading">
        <div className="text-center">
          <img src="/logo.png" alt="Big Effort Swim" className="w-16 h-16 mx-auto animate-pulse" />
          <p className="mt-4 text-lg text-slate-600 font-medium" style={{ fontFamily: 'Nunito' }}>Signing you in...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FDFA] p-4" data-testid="auth-callback-error">
        <Card className="w-full max-w-md rounded-3xl shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito' }}>Sign-In Failed</h2>
            <p className="text-slate-500 mb-6">{error}</p>
            <Button
              onClick={() => { window.location.href = '/login'; }}
              className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold px-8"
              data-testid="back-to-login-btn"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (needsRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FDFA] p-4" data-testid="role-selection">
        <Card className="w-full max-w-md rounded-3xl shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <img src="/logo.png" alt="Big Effort Swim" className="w-14 h-14 mx-auto mb-2" />
            <CardTitle className="text-2xl text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>Welcome to SplashAble!</CardTitle>
            <p className="text-slate-500 mt-1">Select your role to get started</p>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <Button
              data-testid="role-instructor-btn"
              onClick={() => handleRoleSelect('instructor')}
              className="w-full h-16 rounded-2xl bg-[#1E1B4B] hover:bg-[#2D2B66] text-white text-lg font-bold gap-3"
            >
              <UserCog className="w-6 h-6" /> I'm an Instructor
            </Button>
            <Button
              data-testid="role-parent-btn"
              onClick={() => handleRoleSelect('parent')}
              variant="outline"
              className="w-full h-16 rounded-2xl border-2 border-[#22D3EE] text-[#0E7490] hover:bg-cyan-50 text-lg font-bold gap-3"
            >
              <Users className="w-6 h-6" /> I'm a Parent
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
